// App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import './App.css';

// Import Firebase configuration
import './firebase-config';
import { FirebaseProvider } from './contexts/FirebaseContext';

// Import other providers
import { UserProvider } from './contexts/UserContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AuthGuard from './components/AuthGuard';

// Import page components
import LandingPage from './components/LandingPage';
import RegistrationForm from './components/RegistrationForm';
import Home from './components/Home';
import Mentor from './components/Mentor';
import DirectoryEditor from './components/mentorSearch/DirectoryEditor';
import Settings from './components/Settings';
import ProfilePage from './components/ProfilePage';
import Progress from './components/Progress';
import Resources from './components/Resources';
import UnavailablePage from './components/UnavailablePage';

// Import mentor-specific components
import Mentees from './components/Mentees';
import Schedule from './components/Schedule';
import Messages from './components/Messages';

// Optional: Import the debug component for development only
import FirebaseDebug from './components/FirebaseDebug';

function App() {
  const isDebugMode = process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true');

  return (
    // Theme is still outermost, but Firebase comes before User since auth depends on Firebase
    <ThemeProvider>
      <FirebaseProvider>
        <UserProvider>
          <ToastProvider>
            <Router>
              <div className="App">
                {/* Optional Firebase debug component - only in development */}
                {isDebugMode && <FirebaseDebug />}
                
                <Routes>
                  {/* Public routes - no authentication needed */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/register" element={<RegistrationForm />} />
                  
                  {/* Protected routes - require authentication */}
                  <Route path="/app" element={<AuthGuard><Home /></AuthGuard>} />
                  <Route path="/app/mentor/listing" element={<AuthGuard><DirectoryEditor /></AuthGuard>} />
                  <Route path="/app/mentor" element={<AuthGuard><Mentor /></AuthGuard>} />
                  <Route path="/app/settings" element={<AuthGuard><Settings /></AuthGuard>} />
                  <Route path="/app/Settings" element={<AuthGuard><Settings /></AuthGuard>} /> {/* Keep uppercase route for compatibility */}
                  <Route path="/app/profile" element={<AuthGuard><ProfilePage /></AuthGuard>} />
                  <Route path="/app/progress" element={<AuthGuard><Progress /></AuthGuard>} />
                  <Route path="/app/resources" element={<AuthGuard><Resources /></AuthGuard>} />
                  
                  <Route path="/app/courses" element={<AuthGuard><Navigate to="/app/profile?section=courses" replace /></AuthGuard>} />
                  <Route path="/app/clinical" element={<AuthGuard><UnavailablePage title="Clinical Hours" description="Clinical hour logging is not available yet." activeTab="progress" /></AuthGuard>} />
                  <Route path="/app/research" element={<AuthGuard><UnavailablePage title="Research Projects" description="Research project tracking is not available yet." activeTab="progress" /></AuthGuard>} />
                  <Route path="/app/reports/performance" element={<AuthGuard><UnavailablePage title="Clinical Reports" description="Reports will be available after real mentoring activity is connected." /></AuthGuard>} />
                  {/* Mentor-specific routes */}
                  <Route path="/app/mentees" element={<AuthGuard><Mentees /></AuthGuard>} />
                  <Route path="/app/schedule" element={<AuthGuard><Schedule /></AuthGuard>} />
                  <Route path="/app/messages" element={<AuthGuard><Messages /></AuthGuard>} />
                  
                  {/* Redirect any unknown routes to the landing page */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
            </Router>
          </ToastProvider>
        </UserProvider>
      </FirebaseProvider>
    </ThemeProvider>
  );
}

export default App;